import { ChangeEvent, FC, FocusEvent, useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { Icon, IconColor } from "@/components/shared/Icon"
import Tooltip from "@/components/shared/Tooltip"
import { Variant } from "@/types/index"
import "./index.scss"

interface TextInputProps {
   variant?: Variant
   className?: string
   name?: string
   type?: string
   value: string
   onChange: (e: ChangeEvent<HTMLInputElement>) => void
   onBlur?: (e: FocusEvent<HTMLInputElement>) => void
   onFocus?: (e: FocusEvent<HTMLInputElement>) => void
   placeholder?: string
   error?: boolean
   disabled?: boolean
   helperText?: string
   withHelper?: boolean
   autoFocus?: boolean
   min?: number
   onBulkPaste?: (rows: string[]) => void
   testId?: string
   helperTextTestId?: string
}

const TextInput: FC<TextInputProps> = ({
   variant = Variant.DEFAULT,
   className = "",
   name = "",
   type = "text",
   value,
   onChange,
   onFocus = () => {},
   onBlur = () => {},
   placeholder = "",
   error = false,
   disabled = false,
   helperText = "",
   withHelper = false,
   autoFocus = false,
   min,
   onBulkPaste,
   testId,
   helperTextTestId,
}) => {
   const [isFocused, setIsFocused] = useState(false)
   const [inputType, setInputType] = useState(type)
   const passwordHideTimeout = useRef<ReturnType<typeof setTimeout> | null>(
      null
   )
   const switchInputType = () => () => {
      setInputType(inputType === "password" ? "text" : "password")
   }

   useEffect(() => {
      if (type !== "password") return
      const clearPasswordTimeout = () => {
         if (passwordHideTimeout.current) {
            clearTimeout(passwordHideTimeout.current)
            passwordHideTimeout.current = null
         }
      }

      clearPasswordTimeout()

      if (inputType === "text") {
         passwordHideTimeout.current = setTimeout(() => {
            setInputType("password")
            passwordHideTimeout.current = null
         }, 5000)
      }

      return () => clearPasswordTimeout()
   }, [inputType, type])

   return (
      <div className="text-input__wrapper">
         <input
            name={name}
            className={classNames("text-input", {
               [`${className}`]: className,
               "text-input--lg": variant === Variant.LARGE,
               "text-input--focused": isFocused,
               "text-input--error": error,
               "text-input--disabled": disabled,
               "text-input--with-icon": type === "password",
            })}
            type={inputType}
            value={value}
            {...(!disabled && { onChange: (e) => onChange(e) })}
            {...(testId && { "data-testid": testId })}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            onFocus={(e) => {
               setIsFocused(true)
               onFocus?.(e)
            }}
            onBlur={(e) => {
               setIsFocused(false)
               if (onBlur) {
                  onBlur(e)
               }
            }}
            onPaste={(e) => {
               const pastedText = e.clipboardData
                  .getData("text")
                  .split("\n")
                  .filter((row) => row)

               onBulkPaste?.(pastedText)
            }}
            min={min}
            inputMode={type === "number" ? "numeric" : undefined}
         />
         {type === "password" && value.length > 0 && (
            <Tooltip
               key="password__icon-tooltip"
               content={
                  inputType === "password"
                     ? "Password is hidden"
                     : "Password is visible"
               }
            >
               <span className="text-input__icon">
                  <Icon
                     name={inputType === "password" ? "hidden" : "visual"}
                     color={IconColor.HONOLULU_BLUE}
                     size="16"
                     onClick={switchInputType()}
                     disabled={disabled}
                  />
               </span>
            </Tooltip>
         )}
         {withHelper && (
            <div
               className="text-input__helper-text"
               {...(helperTextTestId && { "data-testid": helperTextTestId })}
            >
               {helperText}
            </div>
         )}
      </div>
   )
}

export default TextInput
